/**
 * Stable server clock offset estimator based on realtime push envelopes.
 *
 * Sample convention:
 * - sampleOffsetMs = serverTimeMs - clientNowMs
 * - positive value  => server clock is ahead of local clock
 * - negative value  => server clock is behind local clock
 *
 * Runtime usage:
 * - adjustedNowMs = Date.now() + offsetMs
 */

const SAMPLE_WINDOW_SIZE = 12
const TOP_QUARTILE_FRACTION = 0.25
const EMA_ALPHA = 0.18
const MAX_STEP_PER_UPDATE_MS = 120

function median(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[mid]
  return (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * Picks a robust "low-latency" target from offset samples.
 *
 * Larger sampleOffsetMs usually means lower one-way delivery latency for that message.
 * We avoid raw max() to reduce outlier sensitivity and take median of top quartile.
 */
function pickLowLatencyTarget(samples: number[]): number {
  if (samples.length === 0) return 0
  const sortedAsc = [...samples].sort((a, b) => a - b)
  const topCount = Math.max(
    1,
    Math.ceil(sortedAsc.length * TOP_QUARTILE_FRACTION)
  )
  const topSlice = sortedAsc.slice(sortedAsc.length - topCount)
  return median(topSlice)
}

export class ServerClockOffsetEstimator {
  private samples: number[] = []
  private smoothedOffsetMs = 0
  private hasInitialValue = false

  ingestServerTime(
    serverTimeMs: number,
    clientNowMs: number = Date.now()
  ): number {
    if (!Number.isFinite(serverTimeMs) || !Number.isFinite(clientNowMs)) {
      return this.getOffsetMs()
    }

    const sampleOffsetMs = serverTimeMs - clientNowMs
    this.samples.push(sampleOffsetMs)
    if (this.samples.length > SAMPLE_WINDOW_SIZE) {
      this.samples.shift()
    }

    const targetOffsetMs = pickLowLatencyTarget(this.samples)

    if (!this.hasInitialValue) {
      this.smoothedOffsetMs = targetOffsetMs
      this.hasInitialValue = true
      return this.smoothedOffsetMs
    }

    const emaNext =
      this.smoothedOffsetMs +
      (targetOffsetMs - this.smoothedOffsetMs) * EMA_ALPHA
    const delta = emaNext - this.smoothedOffsetMs
    const clampedDelta = Math.max(
      -MAX_STEP_PER_UPDATE_MS,
      Math.min(MAX_STEP_PER_UPDATE_MS, delta)
    )
    this.smoothedOffsetMs += clampedDelta
    return this.smoothedOffsetMs
  }

  getOffsetMs(): number {
    return this.hasInitialValue ? this.smoothedOffsetMs : 0
  }

  reset(): void {
    this.samples = []
    this.smoothedOffsetMs = 0
    this.hasInitialValue = false
  }
}
